/**
 * 🚨 ProblemAlertsBar — Alerts strip for recurrence, monitoring failure, indicator deviation
 *
 * Displayed at the top of ProblemasPage when alerts exist.
 */

import React, { useState, useCallback } from 'react';
import {
  AlertTriangle, RefreshCw, Activity, X, ChevronDown, ChevronUp, Eye,
} from 'lucide-react';
import type { ProblemAlert, RecurrenceMatch } from '../../types/issue';
import { ALERT_TYPE_LABELS } from '../../types/issue';

interface ProblemAlertsBarProps {
  alerts: ProblemAlert[];
  recurrences: RecurrenceMatch[];
  onDismissAlert: (alertId: string) => void;
  onNavigateToIssue: (issueId: string) => void;
}

const ALERT_ICON: Record<string, React.FC<{ size?: number; className?: string }>> = {
  recurrence: RefreshCw,
  monitoring_failure: AlertTriangle,
  indicator_deviation: Activity,
};

const ALERT_COLOR: Record<string, string> = {
  recurrence: 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20',
  monitoring_failure: 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/20',
  indicator_deviation: 'border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-900/20',
};

const ALERT_TEXT: Record<string, string> = {
  recurrence: 'text-amber-700 dark:text-amber-400',
  monitoring_failure: 'text-red-700 dark:text-red-400',
  indicator_deviation: 'text-orange-700 dark:text-orange-400',
};

export const ProblemAlertsBar: React.FC<ProblemAlertsBarProps> = ({
  alerts,
  recurrences,
  onDismissAlert,
  onNavigateToIssue,
}) => {
  const [expanded, setExpanded] = useState(true);
  const visibleAlerts = alerts.filter(a => !a.dismissed);
  const totalCount = visibleAlerts.length + recurrences.length;

  if (totalCount === 0) return null;

  return (
    <div className="mb-3 rounded border border-amber-200 bg-amber-50/50 dark:border-amber-800/40 dark:bg-amber-900/10">
      {/* Header */}
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <AlertTriangle size={14} className="text-amber-600 dark:text-amber-400" />
        <span className="text-[11px] font-bold text-amber-800 dark:text-amber-300">
          {totalCount} Alerta{totalCount !== 1 ? 's' : ''} Ativo{totalCount !== 1 ? 's' : ''}
        </span>
        {expanded ? (
          <ChevronUp size={12} className="ml-auto text-amber-500" />
        ) : (
          <ChevronDown size={12} className="ml-auto text-amber-500" />
        )}
      </button>

      {expanded && (
        <div className="space-y-1.5 border-t border-amber-200 px-3 pb-3 pt-2 dark:border-amber-800/40">
          {/* Recurrence warnings */}
          {recurrences.map(match => (
            <div
              key={match.issue_id}
              className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-2.5 py-2 dark:border-amber-700 dark:bg-amber-900/20"
            >
              <RefreshCw size={12} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400">
                  Possível Recorrência
                </p>
                <p className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400/80">
                  Similar a &quot;{match.issue_title}&quot; — {Math.round(match.similarity_score * 100)}% similar
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {match.matching_fields.map(field => (
                    <span
                      key={field}
                      className="rounded bg-amber-200 px-1.5 py-0.5 text-[8px] font-semibold uppercase text-amber-800 dark:bg-amber-800/40 dark:text-amber-300"
                    >
                      {field}
                    </span>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onNavigateToIssue(match.issue_id)}
                className="shrink-0 rounded px-2 py-1 text-[9px] font-medium text-amber-700 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-800/30"
              >
                <Eye size={10} className="inline mr-0.5" />
                Ver
              </button>
            </div>
          ))}

          {/* System alerts */}
          {visibleAlerts.map(alert => {
            const Icon = ALERT_ICON[alert.type] ?? AlertTriangle;
            return (
              <div
                key={alert.id}
                className={`flex items-start gap-2 rounded border px-2.5 py-2 ${ALERT_COLOR[alert.type] ?? ''}`}
              >
                <Icon size={12} className={`mt-0.5 shrink-0 ${ALERT_TEXT[alert.type] ?? ''}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-[10px] font-semibold ${ALERT_TEXT[alert.type] ?? ''}`}>
                    {ALERT_TYPE_LABELS[alert.type] ?? alert.type}
                  </p>
                  <p className="mt-0.5 text-[10px] text-slate-600 dark:text-slate-400">
                    {alert.message}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  {alert.related_issue_id && (
                    <button
                      type="button"
                      onClick={() => onNavigateToIssue(alert.related_issue_id!)}
                      className="rounded px-1.5 py-0.5 text-[9px] font-medium text-blue-600 hover:bg-blue-50 dark:text-blue-400"
                    >
                      Ver
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDismissAlert(alert.id)}
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700"
                  >
                    <X size={10} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProblemAlertsBar;
